/**
 * `deque-blocks` (`LN-WGT02`, the contract recorded in the `LN-STL-M4` draft):
 * a `std::deque` as fixed blocks held in order by a map, growing at both ends
 * one block at a time, with the elements already stored never moving.
 *
 * <div class="pc-widget" data-widget="deque-blocks" data-block-size="4"
 *      data-steps="[{&quot;op&quot;:&quot;push_back(10)&quot;,&quot;values&quot;:[10],&quot;blocks&quot;:[[10,null,null,null]],&quot;newBlock&quot;:0,&quot;freed&quot;:false,&quot;mapSize&quot;:8,&quot;mapStart&quot;:3},
 *                   {&quot;op&quot;:&quot;push_front(5)&quot;,&quot;values&quot;:[5,10],&quot;blocks&quot;:[[null,null,null,5],[10,null,null,null]],&quot;newBlock&quot;:0,&quot;freed&quot;:false,&quot;mapSize&quot;:8,&quot;mapStart&quot;:2}]"
 *      data-caption="A block arrives when an element needs a slot in it.">
 *   <p class="pc-widget-fallback">10 sits in one block; push_front(5) adds a block in front of it.</p>
 * </div>
 *
 * `data-block-size` (required): the slots per block. `data-steps` (required):
 * the steps in order, the first frame being step 1, each `{ op, values,
 * blocks, newBlock, freed, mapSize?, mapStart? }`: `op` the call shown,
 * `values` the elements front to back, `blocks` every block in map order as
 * `data-block-size` slots of a value or `null` (empty), `newBlock` the index
 * in `blocks` of a block this step allocated (or `null`), `freed` true when
 * the step freed a block, `mapSize` the map's slot count and `mapStart` the
 * map slot of `blocks[0]` (drawn as the map row when both are given).
 * `values` must be the non-null slots read in order. `data-caption`
 * (optional). A reviewer may add optional fields only.
 */

import {
  asText,
  attachStepper,
  buildShell,
  el,
  isIndex,
  isNonEmptyString,
  isRecord,
  isStringOrNumber,
  liveNote,
  parseJson,
  type WidgetText,
} from "./dom";

type Slot = string | number | null;

interface DequeStep {
  op: string;
  values: (string | number)[];
  blocks: Slot[][];
  newBlock: number | null;
  freed: boolean;
  mapSize?: number;
  mapStart?: number;
}

const isSteps =
  (blockSize: number) =>
  (value: unknown): value is DequeStep[] =>
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((step) => {
      if (
        !isRecord(step) ||
        !isNonEmptyString(step.op) ||
        !Array.isArray(step.values) ||
        !step.values.every(isStringOrNumber) ||
        !Array.isArray(step.blocks) ||
        !step.blocks.every(
          (block) =>
            Array.isArray(block) && block.length === blockSize && block.every((s) => s === null || isStringOrNumber(s)),
        ) ||
        !(step.newBlock === null || (isIndex(step.newBlock) && step.newBlock < step.blocks.length)) ||
        typeof step.freed !== "boolean" ||
        !(step.mapSize === undefined || isIndex(step.mapSize)) ||
        !(step.mapStart === undefined || isIndex(step.mapStart))
      ) {
        return false;
      }
      if (
        step.mapSize !== undefined &&
        step.mapStart !== undefined &&
        step.mapStart + step.blocks.length > step.mapSize
      ) {
        return false;
      }
      // The values are the stored slots read front to back, nothing else.
      const stored = (step.blocks as Slot[][]).flat().filter((s) => s !== null);
      return stored.length === step.values.length && stored.every((s, i) => s === (step.values as unknown[])[i]);
    });

/** More slots than this is not a picture a reader can count. */
const MAX_BLOCK_SIZE = 64;

export function renderDequeBlocks(host: HTMLElement, text: WidgetText): boolean {
  const blockSize = Number(host.dataset.blockSize);
  if (!Number.isInteger(blockSize) || blockSize < 1 || blockSize > MAX_BLOCK_SIZE) return false;
  const steps = parseJson(host.dataset.steps, isSteps(blockSize));
  if (!steps) return false;

  const shell = buildShell(host, {
    label: text("labelDeque"),
    caption: host.dataset.caption,
  });

  const op = el("p", "pc-vec-op");
  const map = el("div", "pc-deque-map");
  const blocks = el("div", "pc-deque-blocks");
  const note = liveNote();
  shell.body.append(op, map, blocks, note);

  attachStepper(
    shell,
    steps.length,
    (index) => {
      const step = steps[index];
      op.textContent = "";
      op.append(el("code", "pc-vec-call", step.op));
      op.append(el("span", "pc-vec-stats", `size() ${step.values.length}`));

      map.textContent = "";
      map.hidden = step.mapSize === undefined || step.mapStart === undefined;
      if (step.mapSize !== undefined && step.mapStart !== undefined) {
        map.append(el("span", "pc-vec-block-label", text("dequeMap")));
        const row = el("div", "pc-widget-cells pc-widget-cells--tight");
        for (let i = 0; i < step.mapSize; i++) {
          const used = i >= step.mapStart && i < step.mapStart + step.blocks.length;
          const slot = el("div", used ? "pc-cell pc-deque-map-slot" : "pc-cell pc-deque-map-slot is-spare");
          slot.append(el("span", "pc-cell-value", used ? "•" : ""));
          if (used && i - step.mapStart === step.newBlock) slot.classList.add("is-copied");
          row.append(slot);
        }
        map.append(row);
      }

      // The first and last stored slots are front() and back().
      const flat = step.blocks.flat();
      const front = flat.findIndex((s) => s !== null);
      let back = -1;
      flat.forEach((s, i) => {
        if (s !== null) back = i;
      });

      blocks.textContent = "";
      step.blocks.forEach((block, b) => {
        const group = el("div", b === step.newBlock ? "pc-vec-block is-new" : "pc-vec-block");
        group.append(el("span", "pc-vec-block-label", text("iterBlock", { n: b + 1 })));
        const row = el("div", "pc-widget-cells pc-widget-cells--tight");
        block.forEach((slot, s) => {
          const at = b * blockSize + s;
          const cell = el("div", slot === null ? "pc-cell is-spare" : "pc-cell");
          if (b === step.newBlock) cell.classList.add("is-copied");
          cell.append(el("span", "pc-cell-value", slot === null ? "" : asText(slot)));
          const marks = [at === front ? "front()" : "", at === back ? "back()" : ""].filter(Boolean).join(" ");
          cell.append(el("span", "pc-cell-cursor", marks));
          row.append(cell);
        });
        group.append(row);
        blocks.append(group);
      });

      const parts: string[] = [];
      if (step.newBlock !== null) parts.push(text("dequeNew"));
      if (step.freed) parts.push(text("dequeFreed"));
      if (parts.length === 0) parts.push(text(step.blocks.length === 0 ? "dequeEmpty" : "dequeSame"));
      note.textContent = parts.join(" ");
    },
    text,
    { liveReadout: false },
  );
  return true;
}
